import { useRef, useMemo, useState, DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { UploadIcon, TrashIcon, RefreshIcon } from "@heroicons/react/outline";
import { SearchInput } from "src/common/components/SearchInput";
import EmptyState from "src/features/statsNew/components/EmptyState";
import ModalConfirmation from "src/common/components/Modal/ModalConfirmation";
import {
  openConfirmationModal,
  closeConfirmationModal,
} from "src/common/state/slice/modal/modalSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { useOrgDocuments } from "src/features/rag/hooks/useOrgDocuments";
import { RagDocument } from "src/features/rag/api/ragApi";
import DocumentAgentAssigner from "src/features/rag/components/DocumentAgentAssigner";

const ACCEPTED_TYPES = ".pdf,.docx,.txt";
const ACCEPTED_MIMES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];
const MAX_SIZE_MB = 50;
const DELETE_DOC_MODAL_ID = "delete-org-document-confirmation";

const formatSize = (bytes: number | string) => {
  const b = Number(bytes);
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

const DocumentsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { documents, loading, upload, deleteDoc, retryDoc, assignToAgent, unassignFromAgent } =
    useOrgDocuments();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const filteredDocuments = useMemo(() => {
    if (!search.trim()) return documents;
    const q = search.toLowerCase();
    return documents.filter(
      (d) =>
        d.filename.toLowerCase().includes(q) ||
        new Date(d.created_at).toLocaleDateString().includes(q),
    );
  }, [documents, search]);

  const readyCount = documents.filter((d) => d.status === "ready").length;
  const processingCount = documents.filter((d) => d.status === "processing").length;
  const errorCount = documents.filter((d) => d.status === "error").length;
  const totalChunks = documents
    .filter((d) => d.status === "ready")
    .reduce((sum, d) => sum + Number(d.chunk_count || 0), 0);
  const totalSizeBytes = documents.reduce((sum, d) => sum + Number(d.size_bytes || 0), 0);
  const MAX_STORAGE_BYTES = 1 * 1024 * 1024 * 1024; // 1 Go
  const storagePercent = Math.max(0.1, (totalSizeBytes / MAX_STORAGE_BYTES) * 100);
  const storagePercentLabel = storagePercent < 1 ? "< 1%" : `${Math.round(storagePercent)}%`;
  const totalSizeLabel =
    totalSizeBytes < 1024
      ? `${totalSizeBytes} o`
      : totalSizeBytes < 1024 * 1024
      ? `${(totalSizeBytes / 1024).toFixed(1)} Ko`
      : `${(totalSizeBytes / (1024 * 1024)).toFixed(1)} Mo`;

  const validateAndUpload = async (file: File) => {
    setError(null);
    if (!ACCEPTED_MIMES.includes(file.type)) {
      setError(t("pages.agents.uploadErrorType"));
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(t("pages.agents.uploadErrorSize", { max: MAX_SIZE_MB }));
      return;
    }
    try {
      await upload(file);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("Document limit")) {
        setError(t("pages.agents.quotaDocsExceeded"));
      } else if (msg.includes("Storage limit")) {
        setError(t("pages.agents.quotaStorageExceeded"));
      } else {
        setError(t("pages.agents.uploadErrorGeneric"));
      }
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndUpload(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteRequest = (docId: number) => {
    setPendingDeleteId(docId);
    dispatch(openConfirmationModal(DELETE_DOC_MODAL_ID));
  };

  const handleDeleteConfirm = async () => {
    if (pendingDeleteId === null) return;
    try {
      await deleteDoc(pendingDeleteId);
      dispatch(setAlert({ type: "success", msg: t("pages.agents.agentDeleted") }));
    } catch {
      setError(t("pages.agents.deleteDocumentError"));
    }
    setPendingDeleteId(null);
    dispatch(closeConfirmationModal(DELETE_DOC_MODAL_ID));
  };

  const handleRetry = async (docId: number) => {
    try {
      await retryDoc(docId);
      dispatch(setAlert({ type: "success", msg: t("pages.agents.documentRetrying") }));
    } catch {
      setError(t("pages.agents.retryDocumentError"));
    }
  };

  const handleQuickUnassign = async (docId: number, agentId: number) => {
    try {
      await unassignFromAgent(docId, agentId);
      dispatch(setAlert({ type: "success", msg: t("pages.agents.documentUnassigned") }));
    } catch {
      console.error("Failed to unassign");
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Action bar */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        {!loading && documents.length > 0 && (
          <div className="flex items-center gap-6">
            {/* Total documents */}
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t("pages.agents.documents")}</p>
                <p className="text-sm font-bold text-gray-900">{documents.length} / 50</p>
              </div>
            </div>
            <div className="w-px h-8 bg-gray-200" />
            {/* Ready */}
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t("pages.agents.statusReady")}</p>
                <p className="text-sm font-bold text-gray-900">{readyCount}</p>
              </div>
            </div>
            {/* Processing */}
            {processingCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-yellow-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-yellow-600 animate-pulse"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t("pages.agents.statusProcessing")}</p>
                  <p className="text-sm font-bold text-gray-900">{processingCount}</p>
                </div>
              </div>
            )}
            {/* Error */}
            {errorCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-red-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t("pages.agents.statusError")}</p>
                  <p className="text-sm font-bold text-red-600">{errorCount}</p>
                </div>
              </div>
            )}
            {/* Chunks */}
            {totalChunks > 0 && <div className="w-px h-8 bg-gray-200" />}
            {totalChunks > 0 && (
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t("pages.agents.totalChunks")}</p>
                  <p className="text-sm font-bold text-gray-900">{totalChunks}</p>
                </div>
              </div>
            )}
            <div className="w-px h-8 bg-gray-200" />
            {/* Storage */}
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                  />
                </svg>
              </div>
              <div className="relative">
                <p className="text-xs text-gray-500">{t("pages.agents.storage")}</p>
                <p className="text-sm font-bold text-gray-900">{totalSizeLabel} / 1 Go</p>
                <p className="absolute text-[10px] text-gray-400">{storagePercentLabel}</p>
              </div>
            </div>
          </div>
        )}
        {(loading || documents.length === 0) && <div />}

        <div className="flex items-center gap-3">
          {!loading && documents.length > 0 && (
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t("general.search") + "..."}
              className="min-w-[220px]"
            />
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
          >
            <UploadIcon className="h-4 w-4" />
            {t("pages.agents.uploadToLibrary")}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Error */}
      {error && (
        <div className="mb-3">
          <p className="text-xs text-red-600" role="alert">
            {error}
          </p>
        </div>
      )}

      {/* Drop zone overlay */}
      {!loading && documents.length === 0 && (
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`flex-1 flex items-center justify-center border-2 border-dashed rounded-xl cursor-pointer transition-all ${
            isDragging
              ? "border-green-500 bg-green-50"
              : "border-gray-300 hover:border-green-400 bg-white"
          }`}
        >
          <EmptyState
            icon="folder"
            title={t("pages.agents.noDocuments")}
            description={t("pages.agents.uploadHint")}
          />
        </div>
      )}

      {/* Document table */}
      {!loading && documents.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex-1">
          {/* Table header */}
          <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-4 py-2.5 grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="col-span-4">{t("pages.agents.name")}</div>
            <div className="col-span-1">{t("general.size")}</div>
            <div className="col-span-1">Chunks</div>
            <div className="col-span-1">{t("pages.agents.addedOn")}</div>
            <div className="col-span-4">{t("pages.agents.assignedAgents")}</div>
            <div className="col-span-1"></div>
          </div>

          <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
            {filteredDocuments.length === 0 ? (
              <EmptyState
                icon="search"
                title={t("statsNew.emptyState.noSearchResults")}
                description={t("statsNew.emptyState.noSearchResultsDescription")}
              />
            ) : (
              filteredDocuments.map((doc: RagDocument) => {
                const docAgents = doc.agents || [];
                const MAX_VISIBLE_AGENTS = 3;
                const visibleAgents = docAgents.slice(0, MAX_VISIBLE_AGENTS);
                const overflowCount = docAgents.length - MAX_VISIBLE_AGENTS;
                const overflowAgents = docAgents.slice(MAX_VISIBLE_AGENTS);
                const isExtracting = doc.status === "processing" && doc.total_chunks_expected === 0;
                const isIndexing = doc.status === "processing" && doc.total_chunks_expected > 0;
                const progressPercent = isIndexing
                  ? Math.round((doc.chunk_count / doc.total_chunks_expected) * 100)
                  : 0;
                return (
                  <div
                    key={doc.id}
                    className="px-4 py-3 grid grid-cols-12 gap-4 items-center border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors text-sm"
                  >
                    {/* Filename + inline status */}
                    <div className="col-span-4 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-gray-900 font-medium" title={doc.filename}>
                          {doc.filename}
                        </span>
                        {/* Inline status badge */}
                        {doc.status === "error" && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-100 flex-shrink-0 cursor-default"
                            title={doc.error_message || t("pages.agents.error")}
                          >
                            {t("pages.agents.error")}
                          </span>
                        )}
                        {isExtracting && (
                          <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 flex-shrink-0">
                            <span className="inline-block w-3 h-3 border-[1.5px] border-yellow-500 border-t-transparent rounded-full animate-spin" />
                            {t("pages.agents.extractingText")}
                          </span>
                        )}
                        {isIndexing && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200 flex-shrink-0 tabular-nums">
                            <span className="inline-block w-3 h-3 border-[1.5px] border-green-500 border-t-transparent rounded-full animate-spin" />
                            {t("pages.agents.indexingChunks")} {progressPercent}%
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Size */}
                    <div className="col-span-1 text-xs text-gray-400">
                      {formatSize(doc.size_bytes)}
                    </div>
                    {/* Chunks */}
                    <div className="col-span-1 text-xs text-gray-400">{doc.chunk_count || 0}</div>
                    {/* Date */}
                    <div className="col-span-1 text-xs text-gray-400">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </div>
                    {/* Agents — interactive pills, max 3 visible + overflow badge */}
                    <div className="col-span-4 flex items-center gap-1 flex-nowrap overflow-hidden">
                      {docAgents.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">
                          {t("pages.agents.noAssignedAgents")}
                        </span>
                      ) : (
                        <>
                          {visibleAgents.map((a) => (
                            <span
                              key={a.id}
                              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200 group/pill flex-shrink-0"
                            >
                              <button
                                onClick={() => navigate(`/agents/${a.id}`)}
                                className="hover:underline max-w-[80px] truncate"
                                title={a.name}
                              >
                                {a.name}
                              </button>
                              <button
                                onClick={() => handleQuickUnassign(doc.id, a.id)}
                                className="p-0.5 rounded-full text-green-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover/pill:opacity-100"
                                aria-label={t("pages.agents.unassignFromAgent")}
                                title={t("pages.agents.removeFromAgent")}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2.5}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </span>
                          ))}
                          {overflowCount > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 border border-gray-200 flex-shrink-0 cursor-default"
                              title={overflowAgents.map((a) => a.name).join(", ")}
                            >
                              +{overflowCount}
                            </span>
                          )}
                        </>
                      )}
                      {/* + button to open agent assigner */}
                      <DocumentAgentAssigner
                        documentId={doc.id}
                        currentAgentIds={docAgents.map((a) => a.id)}
                        onAssign={assignToAgent}
                        onUnassign={unassignFromAgent}
                        trigger={(open) => (
                          <button
                            onClick={open}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs text-gray-400 hover:text-green-600 hover:bg-green-50 border border-dashed border-gray-300 hover:border-green-300 transition-colors flex-shrink-0"
                            title={t("pages.agents.assignToAgent")}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </button>
                        )}
                      />
                    </div>
                    {/* Actions */}
                    <div className="col-span-1 flex items-center justify-end gap-1">
                      {doc.status === "error" && (
                        <button
                          onClick={() => handleRetry(doc.id)}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          aria-label={t("pages.agents.retryDocument")}
                          title={t("pages.agents.retryDocument")}
                        >
                          <RefreshIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteRequest(doc.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        aria-label={t("pages.agents.deleteDocument")}
                        title={t("pages.agents.deleteDocument")}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse flex flex-col items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-xl" />
            <div className="h-4 bg-gray-200 rounded w-32" />
          </div>
        </div>
      )}

      <ModalConfirmation
        id={DELETE_DOC_MODAL_ID}
        title={t("pages.agents.deleteDocument")}
        text={t("pages.agents.deleteDocumentConfirm")}
        onClickSubmit={handleDeleteConfirm}
      />
    </div>
  );
};

export default DocumentsList;
